import React, { useState, useEffect, useMemo } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';

// Utils
import { MESSAGES } from '~/utils/message';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { validURL } from '~/utils/helperFunctions';

// Actions
import {
  createWebhook,
  fetchOneWebhook,
  fetchWebhookEvents,
  updateWebhook,
} from '~/redux/actions/webhooks-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';
import { fetchCustomFieldsCatalog } from '~/redux/actions/customFields-actions';

// Components
import MultiSelect from '~/components/General/MultiSelectNew';
import JsonRenderer from '~/components/Settings/Webhooks/WebhookLog/JsonRenderer';
import LimitReach from '~/components/General/LimitModal';
import OlcButton from '~/components/General/OlcButton';
import OlcTooltip from '~/components/General/OlcTooltip';
import OlcPageBackButton from '~/components/General/OlcPageBackButton';

import { isValidCustomHeaders } from '../customHeaders';
import {
  applyAdditionalFieldsToSample,
  buildAdditionalFieldOptions,
  getSelectedFieldKeys,
  keepAvailableFieldIds,
  toAdditionalFields,
  toSelectedFieldIds,
} from '../additionalFields';

// MUI Components
import { Box, Typography, InputLabel, Input, CircularProgress, GlobalStyles } from '@mui/material';

// Icons
import { InfoWebhookCIcon } from '~/assets/images';

// Styles
import '../styles.scss';

const CreateOrEditWebhook = () => {
  const formId = 'webhook-form';
  const [selectedWebhook, setSelectedWebhook] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState([]);
  const [eventsError, setEventsError] = useState(false);
  const [selectedFieldIds, setSelectedFieldIds] = useState([]);
  // True once this page has fetched the complete custom field list (not just the one from login).
  const [isCatalogLoaded, setIsCatalogLoaded] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [limitModal, setLimitModal] = useState(false);
  const [limitMessage, setLimitMessage] = useState('');

  const params = useParams();
  const navigate = useNavigate();

  const eventOptions = useSelector((state) =>
    state.webhooksReducers.events.map((event, index) => ({
      ...event,
      id: index + 1,
    }))
  );

  const customFields = useSelector((state) => state.customFieldsReducers.allCustomFields);
  const fieldOptions = useMemo(() => buildAdditionalFieldOptions(customFields), [customFields]);
  // Extra fields only apply to events that carry a contact; hide them unless one is selected.
  const showAdditionalFields = eventOptions.some(
    (event) => event.supportsAdditionalFields && selectedEvent.includes(event.title)
  );
  const selectedFields = useMemo(
    () => keepAvailableFieldIds(selectedFieldIds, fieldOptions),
    [selectedFieldIds, fieldOptions]
  );

  const dispatch = useDispatch();

  const validationSchema = yup.object().shape({
    url: yup
      .string()
      .trim()
      .required(MESSAGES.SETTINGS.WEBHOOKS.CREATE.URL_REQUIRED)
      .max(255, MESSAGES.SETTINGS.WEBHOOKS.CREATE.URL_MAX_LENGTH)
      .test('is-valid-url', MESSAGES.SETTINGS.WEBHOOKS.CREATE.URL_INVALID, (value) =>
        value ? validURL(value) : false
      ),
    description: yup
      .string()
      .trim()
      .required(MESSAGES.SETTINGS.WEBHOOKS.CREATE.DESCRIPTION_REQUIRED)
      .max(255, MESSAGES.SETTINGS.WEBHOOKS.CREATE.DESCRIPTION_MAX_LENGTH),
    headers: yup
      .string()
      .trim()
      .nullable(true)
      .test(
        'custom-headers-format',
        MESSAGES.SETTINGS.WEBHOOKS.CREATE.CUSTOM_HEADERS_KEY_VALUE_PAIR,
        isValidCustomHeaders
      )
      .max(255, MESSAGES.SETTINGS.WEBHOOKS.CREATE.CUSTOM_HEADERS_LENGTH),
    slackWebhookUrl: yup
      .string()
      .trim()
      .nullable(true)
      .matches(
        /^https:\/\/hooks\.slack\.com\/services\/T\w+\/B\w+\/\w+$/,
        MESSAGES.SETTINGS.WEBHOOKS.CREATE.SLACK_WEBHOOK_URL_VALIDATION
      ),
  });

  const formik = useFormik({
    initialValues: {
      url: '',
      description: '',
      headers: '',
      slackWebhookUrl: '',
    },
    validationSchema: validationSchema,
  });

  const getOneWebhook = async (id) => {
    if (id) {
      const webbhook = await dispatch(fetchOneWebhook(id));
      if (webbhook.status === 200) {
        setSelectedWebhook(webbhook.data.data);
      }
    }
  };

  const createOrUpdate = async (event) => {
    try {
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!selectedEvent.length) {
        setEventsError(true);
        if (!isValid) {
          return;
        }
        return;
      }
      if (!isValid) {
        return;
      }
      const additionalFields = toAdditionalFields(showAdditionalFields ? selectedFields : []);
      // On edit, only send customFieldIds once the custom field list is known to be complete. The
      // server keeps the stored selection when it is omitted, whereas a list built from a missing or
      // cut-off catalog would overwrite it with fewer ids, or none.
      if (isEdit && !isCatalogLoaded) {
        delete additionalFields.customFieldIds;
      }
      const payload = {
        callbackUrl: formik?.values?.url?.trim(),
        topic: formik?.values?.description?.trim(),
        customHeaders: formik?.values?.headers?.trim(),
        slackWebhookUrl: formik?.values?.slackWebhookUrl?.trim(),
        eventTypes: selectedEvent.length
          ? eventOptions
              .filter((event) => selectedEvent.includes(event.title))
              .map((event) => event.event)
          : undefined,
        ...additionalFields,
      };
      isEdit ? (payload.id = params.id) : undefined;
      setLoading(true);
      const response = await dispatch(isEdit ? updateWebhook(payload) : createWebhook(payload));
      if (response.status === 200) {
        response?.data?.message && dispatch(success(response.data.message));
        navigate('/settings/webhooks');
      } else {
        if (response.status === 418) {
          const errorMessage = response?.data?.message || '';
          setLimitMessage(errorMessage);
          setLimitModal(true);
          setLoading(false);
          return;
        }
        response?.data?.message && dispatch(failure(response?.data?.message));
      }
    } catch (error) {
      setLoading(false);

      // Handle 418 error specifically for limit reached
      if (error?.response?.status === 418) {
        const errorMessage = error?.response?.data?.message || (
          <>
            You've reached your webhook limit! Please upgrade your subscription to add more
            webhooks.
          </>
        );
        setLimitMessage(errorMessage);
        setLimitModal(true);
        return;
      }

      // For other errors, dispatch failure message
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            MESSAGES.GENERAL_ERROR ||
            'An error occurred. Please try again.'
        )
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const { callbackUrl, topic, customHeaders, slackWebhookUrl } = selectedWebhook || {};
    formik.setValues({
      url: callbackUrl || '',
      description: topic || '',
      headers: customHeaders || '',
      slackWebhookUrl: slackWebhookUrl || '',
    });
    setSelectedFieldIds(toSelectedFieldIds(selectedWebhook));
    if (isEdit && selectedWebhook?.eventTypes?.length) {
      setSelectedEvent(
        eventOptions
          .filter((event) => selectedWebhook.eventTypes.includes(event.event))
          .map((event) => event.title)
      );
    }
  }, [selectedWebhook]);

  useEffect(() => {
    if (selectedEvent) {
      setEventsError(false);
    }
  }, [selectedEvent]);

  useEffect(() => {
    if (params?.id?.length) {
      setIsEdit(true);
      getOneWebhook(params?.id);
    }
    Object.keys(formik.initialValues).forEach((fields) => {
      formik.touched[fields] = false;
    });
    dispatch(fetchWebhookEvents());
    // The list from login can be missing (request failed) or cut off (500 per page), so load it here.
    dispatch(fetchCustomFieldsCatalog()).then((response) => {
      const { rows = [], count } = response?.data?.data || {};
      setIsCatalogLoaded(response?.status === 200 && (count == null || rows.length >= count));
    });
    return () => {
      formik.handleReset();
      resetValidation();
    };
  }, []);

  const renderFieldError = (field) =>
    formik.touched[field] && formik.errors[field] ? (
      <Typography
        className="errorMessage"
        sx={{
          margin: '0 !important',
        }}
      >
        <sup>*</sup>
        {formik.errors[field]}
      </Typography>
    ) : null;

  const headersTooltip = (
    <OlcTooltip
      title={
        <Typography className="info-text">
          {MESSAGES.SETTINGS.WEBHOOKS.CREATE.HEADERS_INFO}
        </Typography>
      }
      placement="bottom-start"
    >
      <img src={InfoWebhookCIcon} alt="info" />
    </OlcTooltip>
  );

  const renderWebhookFormFields = () => (
    <Box className="fieldsWrapper">
      <Box className="webhookfield webhookfieldWide">
        <InputLabel className="formLabel">
          {MESSAGES.SETTINGS.WEBHOOKS.CREATE.URL_LABEL}
          <span className="olcRequired">*</span>
        </InputLabel>
        <Input
          type="text"
          placeholder={MESSAGES.SETTINGS.WEBHOOKS.CREATE.URL_PLACEHOLDER}
          autoComplete="off"
          name="url"
          onChange={formik.handleChange}
          className={formik.errors.url && formik.touched.url ? 'invalid formInput' : 'formInput'}
          onBlur={realTimeValidation ? formik.handleBlur : undefined}
          value={formik.values.url}
          onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
        />
        {renderFieldError('url')}
      </Box>
      <Box className="webhookfield">
        <InputLabel className="formLabel">
          {MESSAGES.SETTINGS.WEBHOOKS.CREATE.DESCRIPTION_LABEL}
          <span className="olcRequired">*</span>
        </InputLabel>
        <Input
          type="text"
          placeholder={MESSAGES.SETTINGS.WEBHOOKS.CREATE.DESCRIPTION_PLACEHOLDER}
          autoComplete="off"
          name="description"
          onChange={formik.handleChange}
          className={
            formik.errors.description && formik.touched.description
              ? 'invalid formInput'
              : 'formInput'
          }
          onBlur={realTimeValidation ? formik.handleBlur : undefined}
          value={formik.values.description}
          onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
        />
        {renderFieldError('description')}
      </Box>
      <Box className="webhookfield">
        <InputLabel className="formLabel">
          {MESSAGES.SETTINGS.WEBHOOKS.CREATE.SLACK_WEBHOOK_URL}
        </InputLabel>
        <Input
          type="text"
          placeholder={MESSAGES.SETTINGS.WEBHOOKS.CREATE.SLACK_WEBHOOK_URL}
          autoComplete="off"
          name="slackWebhookUrl"
          onChange={formik.handleChange}
          className={
            formik.errors.slackWebhookUrl && formik.touched.slackWebhookUrl
              ? 'invalid formInput'
              : 'formInput'
          }
          onBlur={realTimeValidation ? formik.handleBlur : undefined}
          value={formik.values.slackWebhookUrl}
          onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
        />
        {renderFieldError('slackWebhookUrl')}
      </Box>
      <Box className="webhookfield">
        <InputLabel className="formLabel customHeadersLabel">
          {MESSAGES.SETTINGS.WEBHOOKS.CREATE.CUSTOM_HEADERS_LABEL}
          {headersTooltip}
        </InputLabel>
        {customHeadersField}
      </Box>
      <Box className="webhookfield">
        <InputLabel className="formLabel">
          {MESSAGES.SETTINGS.WEBHOOKS.CREATE.EVENT_TYPE_LABEL}
          <span className="olcRequired">*</span>
        </InputLabel>
        {eventTypesField}
      </Box>
      {showAdditionalFields && (
        <Box className="webhookfield">
          <InputLabel className="formLabel">
            {MESSAGES.SETTINGS.WEBHOOKS.CREATE.ADDITIONAL_FIELDS_LABEL}
          </InputLabel>
          {additionalFieldsField}
        </Box>
      )}
    </Box>
  );

  const customHeadersField = (
    <Box className="headerFieldsWrapper">
      <Box className="headerfield">
        <Input
          type="text"
          placeholder={MESSAGES.SETTINGS.WEBHOOKS.CREATE.CUSTOM_HEADERS_PLACEHOLDER}
          name="headers"
          autoComplete="off"
          onChange={formik.handleChange}
          className={
            formik.errors.headers && formik.touched.headers ? 'invalid formInput' : 'formInput'
          }
          onBlur={realTimeValidation ? formik.handleBlur : undefined}
          value={formik.values.headers}
          onKeyDown={realTimeValidation ? formik.handleBlur : undefined}
        />
        {renderFieldError('headers')}
      </Box>
    </Box>
  );

  const eventTypesField = (
    <Box className="eventFieldsWrapper">
      <Box className="eventField">
        <div>
          <MultiSelect
            options={eventOptions}
            selectedValue={selectedEvent}
            setSelectedValue={setSelectedEvent}
            simpleSelect={false}
            placeHolderText={MESSAGES.SETTINGS.WEBHOOKS.CREATE.EVENT_PLACEHOLDER}
            component="editwebhook"
          />
          {eventsError && (
            <Typography
              className="errorMessage"
              sx={{
                marginTop: '8px !important',
              }}
            >
              <sup>*</sup>
              {MESSAGES.SETTINGS.WEBHOOKS.CREATE.EVENTS_VALIDATION}
            </Typography>
          )}
        </div>
      </Box>
    </Box>
  );

  const additionalFieldsField = (
    <Box className="eventFieldsWrapper">
      <Box className="eventField">
        <MultiSelect
          options={fieldOptions}
          selectedValue={selectedFields}
          setSelectedValue={setSelectedFieldIds}
          simpleSelect={false}
          placeHolderText={MESSAGES.SETTINGS.WEBHOOKS.CREATE.ADDITIONAL_FIELDS_PLACEHOLDER}
          component="multipleIds"
        />
      </Box>
    </Box>
  );

  const sampleFieldKeys = getSelectedFieldKeys(selectedFields, fieldOptions);

  const samplePayloadField = (
    <Box className="createOrderObjectWrapper">
      {selectedEvent.length > 0 ? (
        eventOptions
          .filter((event) => selectedEvent.includes(event.title))
          .map((event) => {
            const fullEvent = {
              event: event.event,
              data: event.supportsAdditionalFields
                ? applyAdditionalFieldsToSample(
                    event.event,
                    event.example_request_payload,
                    sampleFieldKeys
                  )
                : event.example_request_payload,
              token: '{{WEBHOOK_SECRET_TOKEN}}',
            };

            return (
              <JsonRenderer key={event.event} title={event.title} json={fullEvent} variant="flat" />
            );
          })
      ) : (
        <JsonRenderer json={null} title="No event selected" variant="flat" />
      )}
    </Box>
  );

  const webhookActions = (
    <>
      <OlcButton variant="secondary" onClick={() => navigate('/settings/webhooks')}>
        {MESSAGES.SETTINGS.WEBHOOKS.CREATE.CANCEL_BUTTON}
      </OlcButton>
      <OlcButton
        variant="primary"
        className="btnSave"
        type="submit"
        form={formId}
        onClick={() => validateForm(formik)}
        disabled={loading}
      >
        {loading ? (
          <CircularProgress
            sx={{
              color: 'white',
              width: '20px !important',
              height: '20px !important',
            }}
          />
        ) : (
          MESSAGES.SETTINGS.WEBHOOKS.CREATE.SUBMIT_BUTTON
        )}
      </OlcButton>
    </>
  );

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper webhookEditPageWrapper">
        <Box className="olcScreenCard webhookEditScreenCard">
          <Box className="webhookEditHeader">
            <OlcPageBackButton onClick={() => navigate('/settings/webhooks')}>
              {MESSAGES.SETTINGS.WEBHOOKS.DETAILS.BACK_BUTTON}
            </OlcPageBackButton>
          </Box>
          <form id={formId} className="webhookEditForm" onSubmit={createOrUpdate}>
            <Box className="webhookIsland">
              <Typography className="islandTitle">
                {isEdit
                  ? MESSAGES.SETTINGS.WEBHOOKS.CREATE.EDIT_TITLE
                  : MESSAGES.SETTINGS.WEBHOOKS.CREATE.TITLE}
              </Typography>
              {renderWebhookFormFields()}
            </Box>
            <Box className="webhookIsland">
              <Typography className="islandTitle">
                {MESSAGES.SETTINGS.WEBHOOKS.CREATE.EVENT_SAMPLE_PAYLOAD}
              </Typography>
              {samplePayloadField}
            </Box>
            <Box className="webhookEditFooter">{webhookActions}</Box>
          </form>
        </Box>
      </Box>
      {/* Limit Reached Modal */}
      <LimitReach
        open={limitModal}
        onClose={() => {
          setLimitModal(false);
          setTimeout(() => setLimitMessage(''), 100);
        }}
        message={limitMessage}
        resourceType="webhooks"
      />
    </>
  );
};

export default CreateOrEditWebhook;
